import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AuthShell } from '@/components/branding/auth-shell';
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/reset-password')({ component: ResetPasswordPage });

type Phase = 'verifying' | 'ready' | 'invalid' | 'submitting' | 'done';

function ResetPasswordPage(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('verifying');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Supabase recovery emails redirect with `#access_token=...&refresh_token=...&type=recovery`
  // OR with `?code=...` (PKCE flow). Handle both.
  useEffect(() => {
    void (async () => {
      try {
        const url = new URL(window.location.href);
        const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const code = url.searchParams.get('code');
        const accessToken = hash.get('access_token');
        const refreshToken = hash.get('refresh_token');
        const errorDesc = url.searchParams.get('error_description') ?? hash.get('error_description');

        if (errorDesc) {
          setError(decodeURIComponent(errorDesc));
          setPhase('invalid');
          return;
        }

        if (code) {
          const { error: exErr } = await supabase.auth.exchangeCodeForSession(code);
          if (exErr) {
            setError(exErr.message);
            setPhase('invalid');
            return;
          }
        } else if (accessToken && refreshToken) {
          const { error: setErr } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (setErr) {
            setError(setErr.message);
            setPhase('invalid');
            return;
          }
        } else {
          // No token — check whether we already have a session
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            setError('Reset link is missing or expired. Request a new one from the login screen.');
            setPhase('invalid');
            return;
          }
        }

        setPhase('ready');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not verify reset link');
        setPhase('invalid');
      }
    })();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setPhase('submitting');
    const { error: updErr } = await supabase.auth.updateUser({ password });
    if (updErr) {
      setError(updErr.message);
      setPhase('ready');
      return;
    }

    setPhase('done');
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 1500);
  };

  return (
    <AuthShell>
      <Card className="w-full max-w-sm bg-surface/95 border-border/[0.16] shadow-2xl backdrop-blur-md">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Set a new password</CardTitle>
          <CardDescription>
            {phase === 'verifying' && 'Checking your reset link…'}
            {phase === 'ready' && 'Choose a new password for your SoftSync-AI FLOW account.'}
            {phase === 'submitting' && 'Saving new password…'}
            {phase === 'done' && 'Password saved. Redirecting…'}
            {phase === 'invalid' && 'We could not verify your reset link.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {phase === 'invalid' ? (
            <div className="space-y-3">
              <p className="text-sm text-danger">{error ?? 'Reset link invalid.'}</p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  window.location.href = '/login';
                }}
              >
                Back to login
              </Button>
            </div>
          ) : phase === 'done' ? (
            <p className="text-sm text-success">All set. Taking you to the dashboard…</p>
          ) : (
            <form
              onSubmit={(e) => {
                void onSubmit(e);
              }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={phase !== 'ready'}
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={phase !== 'ready'}
                />
              </div>
              {error ? <p className="text-sm text-danger">{error}</p> : null}
              <Button
                type="submit"
                disabled={phase !== 'ready'}
                className="w-full"
              >
                {phase === 'submitting' ? 'Saving…' : 'Set password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
